/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { StyleSheet, View } from "react-native";
import Icon from "react-icons-kit";
import { ic_info_outline } from "react-icons-kit/md/ic_info_outline";

import { Text, UpduoButton } from "UpduoLibrary";
import ManagerDashboardWidget from "../ManagerDashboardWidget";
import { LocationCategory, SubLocationType } from "../types";
import { isNotMaintenanceUser } from "config/Utilities";
import {
  upduoPrimaryBlack,
  upduoPrimaryBlue,
  upduoLightGray,
} from "config/UpduoColors";
import OnTrackInfoPopup from "./OnTrackInfoPopup";
import LocationRow from "./LocationRow";
import "./manager_dashboard_table.css";
import * as UpduoAnalytics from "config/UpduoAnalytics";

const styles = StyleSheet.create({
  cellStyle: {
    color: upduoPrimaryBlack,
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  horizontalLine: {
    height: 0,
    width: "100%",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: upduoPrimaryBlue,
  },
  formulaRow: { flexDirection: "row", alignItems: "stretch", marginTop: 8 },
  formulaSymbol: { marginHorizontal: 12, fontSize: 18 },
});

export default function SubLocationTableWidget({
  locations,
  locationTypeName,
  loadNextPage,
  loading,
  hasNextPage,
}: {
  locations: SubLocationType[];
  locationTypeName: LocationCategory;
  loadNextPage: () => void;
  loading: boolean;
  hasNextPage: boolean;
}) {
  const [showOnTrackInfoPopup, setShowOnTrackInfoPopup] =
    useState<boolean>(false);
  return (
    <ManagerDashboardWidget title={`quota tracking by ${locationTypeName}`}>
      {locations.length === 0 ? (
        <Text
          style={{
            textAlign: "center",
            color: upduoLightGray,
            minHeight: 40,
          }}
        >{`No ${locationTypeName}s for this location`}</Text>
      ) : (
        <table className={"location-table"}>
          <thead>
            <tr>
              <th className={"ljust"}>
                <Text
                  style={styles.cellStyle}
                >{`${locationTypeName.toUpperCase()}S`}</Text>
              </th>
              <th>
                <Text style={styles.cellStyle}>{"QUOTA COMPLETION"}</Text>
              </th>
              {locationTypeName === "user" ? null : (
                <th>
                  <Text style={styles.cellStyle}>{"PARTICIPATING"}</Text>
                </th>
              )}
              <th>
                <Text style={[styles.cellStyle, { position: "relative" }]}>
                  {"ON TRACK"}
                </Text>
                <Icon
                  size={14}
                  icon={ic_info_outline}
                  style={{
                    marginLeft: 8,
                    color: upduoPrimaryBlue,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setShowOnTrackInfoPopup((current) => !current);
                    UpduoAnalytics.logEvent(
                      "user-managerdashboard-press-formula-calculation"
                    );
                  }}
                />
                <OnTrackInfoPopup
                  isVisible={showOnTrackInfoPopup}
                  onClose={() => {
                    setShowOnTrackInfoPopup(false);
                  }}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {locations
              .filter((x) =>
                locationTypeName === "user"
                  ? isNotMaintenanceUser(x)
                  : x.name !== "Maintenance"
              )
              .sort((a, b) => a.name.localeCompare(b.name))
              .sort((a, b) => {
                // Sort by decreasing quota target
                return (b.quotaTarget || 1) - (a.quotaTarget || 1);
              })
              .sort((a, b) => {
                // Sort by increasing quota completion
                const a_quota_completion =
                  (a.quotaProgress || 0) / (a.quotaTarget || 1);
                const b_quota_completion =
                  (b.quotaProgress || 0) / (b.quotaTarget || 1);
                return a_quota_completion - b_quota_completion;
              })
              .map((x, index) => (
                <LocationRow
                  location={x}
                  locationType={locationTypeName}
                  key={x.id}
                  index={index}
                />
              ))}
          </tbody>
        </table>
      )}
      {hasNextPage ? (
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-around",
            marginTop: 20,
          }}
        >
          <UpduoButton
            text={"Load More"}
            onPress={loadNextPage}
            loading={loading}
            style={{ minWidth: 164 }}
          />
        </View>
      ) : null}
    </ManagerDashboardWidget>
  );
}
